/* eslint-disable @typescript-eslint/no-use-before-define */
import classnames from 'classnames';
import React, { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { customDateStr } from '../../../../shared/misc/utils';
import {
  GrievancePhase,
  GrievanceMilestone,
  GRIEVANCE_STATUSES,
} from '../../../../services/types/Grievance';
import Context from '../context/DetailsContext';
import UIKitIcon from '../../../../shared/UIKITComponents/Icon';

interface TimelineData {
  currentPhaseIndex: number;
  activePhaseMilestoneIndex: number;
  activePhase: GrievancePhase | undefined;
  currentMilestoneIndex: number;
  currentMilestone: GrievanceMilestone | undefined;
  lastPhaseName: string;
  lastMilestonePhaseIndex: number;
  allCompleted: boolean;
  todayBeforeActivePhase: boolean;
}

const GrievanceTimeline: React.FC = () => {
  const { grievancePhaseData, phases } = React.useContext(Context);
  const currentPhase = grievancePhaseData.currentPhaseName;

  const { t } = useTranslation();

  const isNotApplicable = grievancePhaseData.currentStatus === GRIEVANCE_STATUSES.NOT_APPLICABLE;

  const {
    currentPhaseIndex,
    activePhaseMilestoneIndex,
    activePhase,
    currentMilestoneIndex,
    currentMilestone,
    lastPhaseName,
    allCompleted,
    todayBeforeActivePhase,
  } = initData(grievancePhaseData.currentStatus, phases, currentPhase);

  const showToday = !allCompleted && !isNotApplicable;

  return (
    <div className="grievance-timeline-wrapper table-responsive mb-4">
      <div className="grievance-timeline">
        {phases.map((phase, phaseIndex) => {
          const isCurrentPhase = phaseIndex === currentPhaseIndex;
          const isPreviousPhase = phaseIndex < activePhaseMilestoneIndex;
          const isActivePhase = activePhase != null && phase.phase === activePhase.phase;
          const isSkippedPhase =
            phaseIndex < currentPhaseIndex &&
            phase.milestones.filter((m) => m.showInTimeline).some((m) => m.date === null);

          return (
            <Fragment key={`grievance_timeline_step_${phase.phase}`}>
              <div
                className={`gt-step-item ${classnames({
                  'gt-current': isActivePhase,
                  'gt-previous': isPreviousPhase,
                  'gt-step-item--wide': phase.milestones.filter((m) => m.showInTimeline).length > 1,
                })}`}
              >
                {/* Phase header */}
                <div className={`gt-phase-name${isCurrentPhase ? ' gt-phase-name--active' : ''}`}>
                  {t(`grievanceStages.${phase.phase}`, phase.phase)}
                </div>

                {/* Milestones row */}
                <div className="gt-milestones">
                  {/* Start icon — only on the first phase */}
                  {phaseIndex === 0 && (
                    <div className="gt-milestone-item gt-milestone-item--start">
                      <div className="gt-flow-line" />
                      <div className="gt-start-icon">
                        <UIKitIcon name="description" size="m" />
                      </div>
                    </div>
                  )}
                  {phase.milestones
                    .filter((m) => m.showInTimeline)
                    .map((milestone, mIndex) => {
                      const isGlobalLastMilestone =
                        currentMilestone != null && milestone.code === currentMilestone.code;
                      const isCurrentMilestone = isActivePhase && isGlobalLastMilestone;
                      const isPreviousMilestone = isActivePhase
                        ? mIndex < currentMilestoneIndex
                        : isPreviousPhase;

                      // Today goes AFTER the last globally completed milestone
                      // when it belongs to the same phase as currentPhase
                      const showTodayAfterThis =
                        showToday && isGlobalLastMilestone && !todayBeforeActivePhase;

                      // Determine if the last completed milestone is the last visible one in its phase
                      const isLastMilestoneInPhase =
                        mIndex === phase.milestones.filter((m) => m.showInTimeline).length - 1;

                      // Today after last milestone before phase boundary → 75% (midpoint dot↔boundary)
                      const showTodayAfterLast = showTodayAfterThis && isLastMilestoneInPhase;
                      // Today between two milestones → 100% (midpoint between adjacent dots)
                      const showTodayAfterMid = showTodayAfterThis && !isLastMilestoneInPhase;

                      // Today goes BEFORE the first milestone of the active phase
                      // when currentPhase is ahead of where the last milestone sits
                      const showTodayBeforeFirst =
                        showToday && isActivePhase && mIndex === 0 && todayBeforeActivePhase;

                      const isSkippedMilestone =
                        milestone.date === null &&
                        (isSkippedPhase ||
                          grievancePhaseData.currentStatus === GRIEVANCE_STATUSES.CLOSED);

                      return (
                        <Fragment
                          key={`grievance_timeline_milestone_${phase.phase}_${milestone.code}`}
                        >
                          <div
                            className={`gt-milestone-item ${classnames({
                              'gt-milestone--current':
                                isCurrentMilestone ||
                                (isNotApplicable && phaseIndex === currentPhaseIndex),
                              'gt-milestone--previous': isPreviousMilestone,
                              'gt-milestone--last-phase': phase.phase === lastPhaseName,
                              'gt-milestone--has-today-before': showTodayBeforeFirst,
                              'gt-milestone--has-today-after': showTodayAfterLast,
                              'gt-milestone--has-today-after-mid': showTodayAfterMid,
                              'gt-skipped': isSkippedMilestone,
                            })}`}
                          >
                            {/* Today placed inside the first active-phase milestone as absolute,
                              centered at 25% = midpoint between phase boundary and milestone dot */}
                            {showTodayBeforeFirst && (
                              <div className="gt-today-before">
                                <div className="gt-today-dot" />
                                <div className="gt-today-connector" />
                                <div className="gt-today-label">
                                  <span>{t('grievanceTimeline.today', 'Today')}</span>
                                </div>
                              </div>
                            )}
                            {/* Today placed inside the last active-phase milestone as absolute.
                              75% = midpoint between dot and phase boundary (last in phase).
                              100% = midpoint between adjacent milestone dots (between milestones). */}
                            {(showTodayAfterLast || showTodayAfterMid) && (
                              <div className="gt-today-after">
                                <div className="gt-today-dot" />
                                <div className="gt-today-connector" />
                                <div className="gt-today-label">
                                  <span>{t('grievanceTimeline.today', 'Today')}</span>
                                </div>
                              </div>
                            )}
                            <div className="gt-flow-line" />
                            <div
                              className={`gt-dot ${classnames({
                                'gt-dot--done': isPreviousMilestone || isCurrentMilestone,
                                'gt-dot--pending': !isPreviousMilestone && !isCurrentMilestone,
                              })}`}
                            />
                            <div className="gt-connector-line" />
                            <div className="gt-milestone-label">
                              <span className="gt-milestone-name">{milestone.name}</span>
                              {milestone.date && (
                                <span className="gt-milestone-date">
                                  {customDateStr(milestone.date, 'dd-MM-yyyy')}
                                </span>
                              )}
                            </div>
                          </div>
                        </Fragment>
                      );
                    })}

                  {/* Empty phase spacer */}
                  {phase.milestones.filter((m) => m.showInTimeline).length === 0 && (
                    <div className="gt-milestone-item">
                      <div className="gt-flow-line" />
                    </div>
                  )}

                  {/* Not Applicable icon at the end of Applicability phase */}
                  {phaseIndex === 0 && isNotApplicable && (
                    <div className="gt-milestone-item gt-milestone-item--icon gt-times-circle-icon not-applicable">
                      <div className="gt-flow-line" />
                      <div className="gt-times-circle-icon--not-applicable">
                        <UIKitIcon name="close" size="m" />
                      </div>
                    </div>
                  )}

                  {/* Closed checkmark icon at the very end */}
                  {phaseIndex === phases.length - 1 && (
                    <div
                      className={`gt-milestone-item gt-milestone-item--icon ${classnames({
                        'gt-milestone--done-icon': allCompleted,
                      })}`}
                    >
                      <div className="gt-flow-line" />
                      <div className={`gt-check-icon ${allCompleted ? 'gt-check-icon--done' : ''}`}>
                        <UIKitIcon name="done" size="m" />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </Fragment>
          );
        })}
      </div>
    </div>
  );
};

const initData = (
  currentStatus: string,
  phases: GrievancePhase[],
  currentPhase: 'Applicability' | 'A&R' | 'Monitoring' | 'Closed' | 'N/A'
): TimelineData => {
  let currentPhaseIndex = phases.map((x) => x.phase).indexOf(currentPhase);

  // Find the last milestone that has a date across all phases
  const allMilestones = phases.flatMap((p) => p.milestones).filter((m) => m.showInTimeline);
  const completedMilestones = [...allMilestones].filter((m) => m.date !== null);
  const currentMilestone: GrievanceMilestone | undefined =
    completedMilestones.length > 0
      ? completedMilestones[completedMilestones.length - 1]
      : undefined;

  const lastPhaseName = phases[phases.length - 1].phase;

  // Find which phase contains the current milestone
  let activePhase: GrievancePhase | undefined = currentMilestone
    ? phases.find((p) => p.milestones.some((m) => m.code === currentMilestone.code))
    : undefined;

  let activePhaseMilestoneIndex = activePhase
    ? phases.map((x) => x.phase).indexOf(activePhase.phase)
    : -1;

  // Capture BEFORE the override so we know if currentPhase is ahead of the last milestone
  const lastMilestonePhaseIndex = activePhaseMilestoneIndex;

  // If the currentPhase (from server) is ahead of where the last milestone sits, use currentPhase
  if (currentPhaseIndex > activePhaseMilestoneIndex) {
    activePhase = phases[currentPhaseIndex];
    activePhaseMilestoneIndex = currentPhaseIndex;
  }

  // Today goes before the active phase's first milestone when currentPhase is ahead
  // of where the last completed milestone lives (or when no milestone has a date yet)
  const todayBeforeActivePhase = currentPhaseIndex > lastMilestonePhaseIndex;

  // Index of the current milestone within the active phase (only visible milestones)
  const currentMilestoneIndex =
    activePhase && currentMilestone
      ? activePhase.milestones
          .filter((m) => m.showInTimeline)
          .map((m) => m.code)
          .indexOf(currentMilestone.code)
      : -1;

  // All completed when every milestone across all phases has a date
  const allCompleted =
    (allMilestones.length > 0 && allMilestones.every((m) => m.date !== null)) ||
    currentStatus === GRIEVANCE_STATUSES.CLOSED; // allMilestones already filtered by showInTimeline

  if (currentStatus === GRIEVANCE_STATUSES.NOT_APPLICABLE) {
    currentPhaseIndex = 0;
  }

  return {
    currentPhaseIndex,
    activePhaseMilestoneIndex,
    activePhase,
    currentMilestoneIndex,
    currentMilestone,
    lastPhaseName,
    lastMilestonePhaseIndex,
    allCompleted,
    todayBeforeActivePhase,
  };
};

export default GrievanceTimeline;
